import {
  LayoutDashboard,
  Target,
  Laptop,
  Play,
  AlertTriangle,
  FileText,
  History,
  ScrollText,
  KeyRound,
  Power,
  Users,
  Github,
  Share2,
  Router,
} from 'lucide-react';
import type { Action, Role } from '../../lib/rbac';
import { can } from '../../lib/rbac';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  requires?: Action;
}

/** Everyday items, always visible — kept to 5 so a non-technical user isn't presented with a wall
 * of options. "Scan" (scan HISTORY — starting one is the separate, always-visible orange "New
 * Scan" button above this list) replaces the old Runs + Retests items; "Devices" replaces Internal
 * Testing + Endpoint Testing (see Devices.tsx's Network/Endpoints tabs). */
export const PRIMARY_NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/scans', label: 'Scan', icon: Play, requires: 'run:read' },
  { to: '/findings', label: 'Findings', icon: AlertTriangle, requires: 'finding:read' },
  { to: '/reports', label: 'Reports', icon: FileText, requires: 'report:read' },
  { to: '/devices', label: 'Endpoint Agents', icon: Laptop, requires: 'internalAgent:read' },
];

/** Less-prominent items a power user still reaches often, but that don't belong in the first 5. */
export const SECONDARY_NAV_ITEMS: NavItem[] = [
  { to: '/attack-paths', label: 'Attack Paths', icon: Share2, requires: 'finding:read' },
  { to: '/audit-log', label: 'Audit Log', icon: ScrollText, requires: 'audit:read' },
  { to: '/users', label: 'Users & Workspace', icon: Users, requires: 'user:manage' },
  { to: '/kill-switch', label: 'Kill Switch', icon: Power, requires: 'killSwitch:read' },
];

/** Configuration-heavy / power-user items, tucked under a collapsible "Advanced" section. */
export const ADVANCED_NAV_ITEMS: NavItem[] = [
  { to: '/targets', label: 'Targets', icon: Target, requires: 'target:read' },
  // Asset Groups, Scope Policies, and Engagements were removed from the nav (their routes still
  // work, just unlinked from the sidebar) — see nav cleanup brief.
  { to: '/github', label: 'Code Scanning', icon: Github, requires: 'engagement:read' },
  // "My API Keys" (not just "API Keys") to distinguish this tenant-scoped, self-service key from
  // the BUI-issued Platform API keys managed at /admin/platform-keys (a different system).
  { to: '/api-keys', label: 'My API Keys', icon: KeyRound, requires: 'apiKey:manage' },
  { to: '/retests', label: 'Retests', icon: History, requires: 'run:read' },
  { to: '/brokers', label: 'Network Agents', icon: Router, requires: 'broker:read' },
];

/** role undefined (API-key auth) => show everything; the API is still the real gate. */
export function visibleNavItems(items: NavItem[], role: Role | undefined): NavItem[] {
  if (role === undefined) return items;
  return items.filter((item) => !item.requires || can(role, item.requires));
}
